import { z } from 'zod';

// Access mode enum matching Prisma AccessMode
export const AccessModeSchema = z.enum(['ALL', 'INCLUDE', 'EXCLUDE']);
export type AccessMode = z.infer<typeof AccessModeSchema>;

// MongoDB ObjectId: 24-character hex string
const ObjectIdSchema = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid ObjectId');

const MAX_APPLICATION_IDS = 500;

// Error response schema
export const ErrorResponseSchema = z.object({
  message: z.string(),
});

// User application access configuration
export const UserApplicationAccessSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  targetTenantId: z.string(),
  userId: z.string(),
  mode: AccessModeSchema,
  applicationIds: z.array(z.string()),
  updatedAt: z.iso.datetime(),
});

export type UserApplicationAccess = z.infer<typeof UserApplicationAccessSchema>;

// Response schema for API - includes user info
export const UserApplicationAccessResponseSchema =
  UserApplicationAccessSchema.extend({
    // Optional user display name (resolved from Descope)
    userName: z.string().optional(),
    userEmail: z.string().optional(),
  });

export type UserApplicationAccessResponse = z.infer<
  typeof UserApplicationAccessResponseSchema
>;

// Create/Update request body
export const SetUserApplicationAccessSchema = z.object({
  userId: z.string().min(1, 'User ID is required'),
  mode: AccessModeSchema,
  applicationIds: z.array(ObjectIdSchema).max(MAX_APPLICATION_IDS).default([]),
  targetTenantId: z.string().optional(),
});

export type SetUserApplicationAccessInput = z.infer<
  typeof SetUserApplicationAccessSchema
>;

// Update request body (for PUT/PATCH)
export const UpdateUserApplicationAccessSchema = z.object({
  mode: AccessModeSchema,
  applicationIds: z.array(ObjectIdSchema).max(MAX_APPLICATION_IDS),
  targetTenantId: z.string().optional(),
});

export type UpdateUserApplicationAccessInput = z.infer<
  typeof UpdateUserApplicationAccessSchema
>;

// Bulk set request body
export const BulkSetUserApplicationAccessSchema = z.object({
  configs: z.array(SetUserApplicationAccessSchema),
});

export type BulkSetUserApplicationAccessInput = z.infer<
  typeof BulkSetUserApplicationAccessSchema
>;

// List response
export const UserApplicationAccessListResponseSchema = z.object({
  items: z.array(UserApplicationAccessResponseSchema),
  total: z.number(),
});

export type UserApplicationAccessListResponse = z.infer<
  typeof UserApplicationAccessListResponseSchema
>;

// Computed access config (what the DAL uses)
export const ApplicationAccessConfigSchema = z.object({
  mode: AccessModeSchema,
  applicationIds: z.array(z.string()),
});

export type ApplicationAccessConfig = z.infer<
  typeof ApplicationAccessConfigSchema
>;

// Application summary for access assignment UI
export const ApplicationSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type ApplicationSummary = z.infer<typeof ApplicationSummarySchema>;

export const ApplicationSummaryListResponseSchema = z.object({
  items: z.array(ApplicationSummarySchema),
});

export type ApplicationSummaryListResponse = z.infer<
  typeof ApplicationSummaryListResponseSchema
>;
